import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ClipboardCheck, Printer, Loader2, Send } from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";

const STATUS_LABEL = { draft: "Draft", sent: "Sent to Warehouse", pulled: "Pulled", delivered: "Delivered Onsite" };

// Cluster 16A — generate a picking ticket for a location and list this
// project's tickets (Doc Control / Material).
export default function PickingTicketDialog({ projectId, open, onOpenChange, scopes = [],
                                             buildings = [], selectedRoomKeys = [], mobilizationId = "" }) {
  const [tab, setTab] = useState("new");
  const [form, setForm] = useState({ level: "building", building: "", floor: "", area: "", scope_types: [], needed_by: "", requested_by: "", notes: "" });
  const [preview, setPreview] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [busy, setBusy] = useState(false);

  const actor = localStorage.getItem("fieldops-installer-name") || "";
  const body = useMemo(() => ({
    project_id: projectId, level: form.level, building: form.building, floor: form.floor,
    area: form.area, scope_types: form.scope_types,
    room_keys: form.level === "rooms" ? selectedRoomKeys : [],
    needed_by: form.needed_by, requested_by: form.requested_by || actor,
    mobilization_id: mobilizationId, notes: form.notes,
    // eslint-disable-next-line
  }), [projectId, form, selectedRoomKeys, mobilizationId]);

  const loadTickets = async () => {
    try {
      const r = await api.get("/picking-tickets", { params: { project_id: projectId } });
      setTickets(r.data || []);
    } catch { setTickets([]); }
  };
  useEffect(() => { if (open) { loadTickets(); setForm((f) => ({ ...f, requested_by: f.requested_by || actor, level: selectedRoomKeys.length ? "rooms" : f.level })); } /* eslint-disable-next-line */ }, [open, projectId]);

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/picking-tickets/preview", body);
      setPreview(r.data);
      // Needed-by defaults to the location's Start date.
      if (r.data.needed_by && !form.needed_by) setForm((f) => ({ ...f, needed_by: r.data.needed_by }));
      if (!(r.data.lines || []).some((l) => l.qty_requested > 0)) {
        toast.info("Nothing left to pull for that selection");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not work out the quantities"); }
    finally { setBusy(false); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/picking-tickets", body);
      toast.success(`Ticket ${r.data.ticket_number} created`);
      setPreview(null); setTab("list"); await loadTickets();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create the ticket");
    } finally { setBusy(false); }
  };

  const send = async (t) => {
    try {
      await api.patch(`/picking-tickets/${t.id}`, { status: "sent", by: actor, note: "Sent from the job" });
      toast.success(`${t.ticket_number} sent to the warehouse`);
      loadTickets();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the ticket"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="picking-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-blue-700" /> Pull tickets
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 mb-1">
          {[["new", "Generate"], ["list", `This job (${tickets.length})`]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`picking-tab-${k}`}
                    className={`h-8 px-3 text-xs rounded border ${tab === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "new" && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-gray-500">Location</Label>
                <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}
                        data-testid="picking-level" className="h-8 w-full text-xs border border-gray-300 rounded px-1 bg-white">
                  <option value="building">Building</option>
                  <option value="floor">Floor</option>
                  <option value="area">Area</option>
                  <option value="rooms">{`Selected rooms (${selectedRoomKeys.length})`}</option>
                  <option value="project">Whole job</option>
                </select>
              </div>
              {["building", "floor", "area"].includes(form.level) && (
                <div>
                  <Label className="text-[10px] uppercase text-gray-500">Building</Label>
                  <select value={form.building} onChange={(e) => setForm({ ...form, building: e.target.value })}
                          data-testid="picking-building" className="h-8 w-full text-xs border border-gray-300 rounded px-1 bg-white">
                    <option value="">— pick —</option>
                    {buildings.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              )}
              {["floor", "area"].includes(form.level) && (
                <div>
                  <Label className="text-[10px] uppercase text-gray-500">Floor</Label>
                  <Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })}
                         data-testid="picking-floor" className="h-8 text-xs" />
                </div>
              )}
              {form.level === "area" && (
                <div>
                  <Label className="text-[10px] uppercase text-gray-500">Area</Label>
                  <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
                         data-testid="picking-area" className="h-8 text-xs" />
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase text-gray-500">Needed by</Label>
                <Input type="date" value={form.needed_by} onChange={(e) => setForm({ ...form, needed_by: e.target.value })}
                       data-testid="picking-needed-by" className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-gray-500">Requested by</Label>
                <Input value={form.requested_by} onChange={(e) => setForm({ ...form, requested_by: e.target.value })}
                       data-testid="picking-requested-by" className="h-8 text-xs" />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <span className="text-[10px] uppercase text-gray-500">Scopes</span>
              {scopes.map((s) => (
                <label key={s} className="text-[11px] flex items-center gap-1" data-testid={`picking-scope-${s}`}>
                  <input type="checkbox" checked={form.scope_types.includes(s)}
                         onChange={() => setForm({ ...form, scope_types: form.scope_types.includes(s)
                           ? form.scope_types.filter((x) => x !== s) : [...form.scope_types, s] })} />
                  {s}
                </label>
              ))}
              <span className="text-[10px] text-gray-400">none ticked = every scope</span>
            </div>
            <div className="flex gap-2">
              <Button variant="internal" size="sm" className="h-8" onClick={runPreview} disabled={busy} data-testid="picking-preview-btn">
                {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Work out quantities
              </Button>
              {preview && (preview.lines || []).some((l) => l.qty_requested > 0) && (
                <Button size="sm" className="h-8 bg-blue-600 text-white" onClick={create} disabled={busy}
                        data-testid="picking-create-btn">Generate picking ticket</Button>
              )}
            </div>

            {preview && (
              <div className="border border-gray-200 rounded overflow-hidden" data-testid="picking-preview">
                <div className="px-2 py-1 bg-gray-50 text-[11px] text-gray-600">
                  {preview.location_label} · {preview.rooms} room(s) · needed by {preview.needed_by || "—"}
                  {preview.rooms === 0 && (
                    <span className="text-red-700 font-semibold" data-testid="picking-no-rooms">
                      {" "}— nothing matched that selection, pick rooms or a different location
                    </span>
                  )}
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 text-gray-600 uppercase text-[10px]">
                    <tr><th className="text-left p-1">Scope</th><th className="text-left p-1">Code</th>
                      <th className="text-left p-1">Description</th><th className="text-left p-1">Unit</th>
                      <th className="text-right p-1">FRS</th><th className="text-right p-1">Installed</th>
                      <th className="text-right p-1">On tickets</th><th className="text-right p-1">To pull</th></tr>
                  </thead>
                  <tbody>
                    {(preview.lines || []).map((l) => (
                      <tr key={`${l.scope_type}|${l.product_code}`} className="border-t border-gray-100"
                          data-testid={`picking-line-${l.product_code}`}>
                        <td className="p-1">{l.scope_type}</td>
                        <td className="p-1 font-mono">{l.product_code}</td>
                        <td className="p-1">{l.product_description}</td>
                        <td className="p-1">{l.unit_of_measure}</td>
                        <td className="p-1 text-right tabular-nums">{l.frs_quantity}</td>
                        <td className="p-1 text-right tabular-nums">{l.installed}</td>
                        <td className="p-1 text-right tabular-nums">{l.on_open_tickets}</td>
                        <td className="p-1 text-right tabular-nums font-bold">{l.qty_requested}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-2 py-1 text-[10px] text-gray-500">
                  To pull = FRS quantity − installed − quantity already on a live ticket for the same room and product.
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "list" && (
          <table className="w-full text-xs" data-testid="picking-ticket-list">
            <thead className="bg-gray-100 text-gray-600 uppercase text-[10px]">
              <tr><th className="text-left p-1">Ticket</th><th className="text-left p-1">Location</th>
                <th className="text-left p-1">Needed by</th><th className="text-right p-1">Lines</th>
                <th className="text-right p-1">Units</th><th className="text-left p-1">Status</th><th className="p-1"></th></tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} className="border-t border-gray-100" data-testid={`picking-ticket-${t.ticket_number}`}>
                  <td className="p-1 font-mono">{t.ticket_number}</td>
                  <td className="p-1">{t.location_label}</td>
                  <td className="p-1">{t.needed_by || "—"}</td>
                  <td className="p-1 text-right tabular-nums">{(t.lines || []).length}</td>
                  <td className="p-1 text-right tabular-nums">{(t.lines || []).reduce((a, l) => a + (l.qty_requested || 0), 0)}</td>
                  <td className="p-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      t.status === "delivered" ? "bg-emerald-100 text-emerald-800"
                        : t.status === "pulled" ? "bg-blue-100 text-blue-800"
                        : t.status === "sent" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </span>
                  </td>
                  <td className="p-1 text-right whitespace-nowrap">
                    {t.status === "draft" && (
                      <Button size="sm" variant="outline" className="h-7 mr-1" onClick={() => send(t)}
                              data-testid={`picking-send-${t.ticket_number}`}>
                        <Send className="w-3 h-3 mr-1" /> Send to warehouse
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-7"
                            onClick={() => window.open(`${API}/picking-tickets/${t.id}/print`, "_blank")}
                            data-testid={`picking-print-${t.ticket_number}`}>
                      <Printer className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
              {tickets.length === 0 && (
                <tr><td colSpan={7} className="p-2 text-center text-gray-400" data-testid="picking-empty">
                  No tickets on this project yet.</td></tr>
              )}
            </tbody>
          </table>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="picking-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
